import React, { useEffect, useRef, useState } from 'react';
import { withSearch } from '@elastic/react-search-ui';
import { css } from '@linaria/core';
import { useMediaQuery } from 'react-responsive';
import ReactGA from 'react-ga4';
import Container from './Container';
import FormTextInput from './FormTextInput';
import SearchIcon from '../icons/Search';
import CloseIcon from '../icons/Close';
import Button from './Button';
import LocationSearch from './LocationSearch';
import { getSearchType } from '../utils/dataLayer';
import { MAP_VIEWPORT_FILTER_FIELDS } from '../utils/mapFilters';
import { useMapState } from '../contexts/MapStateContext';

const wrapper = css`
  margin-bottom: 20px;

  @media (min-width: 1080px) {
    margin-bottom: 40px;
  }
`;
const header = css`
  background: #004258;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
`;
const titleStyle = css`
  margin: 0 0 5px;
  color: white;
  font-size: 26px;
  line-height: 38px;
  font-weight: 600;

  @media (min-width: 800px) {
    margin: 0 0 10px;
    font-size: 34px;
    line-height: 46px;
    font-weight: 800;
  }
`;

const subTitleStyle = css`
  color: #fff;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  margin-bottom: 10px;

  p {
    color: #fff;
    font-size: 18px;

    &:first-child {
      margin-top: 0;
    }

    &:not(:last-child) {
      margin-bottom: 20px;
    }
  }

  ul {
    list-style: none;
    padding-left: 0;
    margin: 0 0 30px 20px;

    li {
      position: relative;
      padding-left: 25px;
      margin-bottom: 20px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 5px;
        height: 5px;
        background-color: #1a1a1a;
      }
    }
  }

  a {
    color: #fff;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;

    &:hover {
      color: #fff;
      text-decoration-thickness: 2px;
      text-underline-offset: 2px;
    }

    &:visited {
      color: #fff;
    }
  }

  @media (min-width: 800px) {
    font-size: 18px;
    line-height: 30px;
  }

  @media (min-width: 1000px) {
    max-width: 70ch;
  }
`;

const fieldLabel = css`
  display: block;
  color: #fff;
  font-weight: 400;
  font-size: 18px;
  line-height: 30px;
  margin-bottom: 10px;
`;

const searchWrapper = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 5px;

  @media (min-width: 800px) {
    margin-top: 10px;
  }
`;
const searchForm = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  width: 100%;

  @media (max-width: 799px) {
    margin-bottom: 20px;
    width: auto;
  }
`;
const searchInput = css`
  font-size: 18px;
  height: 52px;
  padding: 11px 50px 11px 50px;
`;
const searchFormButton = css`
  flex: 0;
  padding: 10px 14px !important;

  svg {
    width: 20px;
    height: 20px;
    margin-right: 15px;

    @media (min-width: 800px) {
      margin-right: 0;
    }
  }

  span {
    @media (min-width: 800px) {
      display: none;
    }
  }
`;

const searchInputWrapper = css`
  width: 100%;
`;

const searchInputIcon = css`
  width: 20px;
  height: 20px;
  position: absolute;
  margin-left: 15px;
  margin-top: 14px;
`;

const searchInputInnerWrapper = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  @media (min-width: 800px) {
    flex-direction: row;
    align-items: flex-end;
  }
`;

const fieldColumn = css`
  flex: 424 1 0;
  min-width: 0;
`;

const searchInputContainer = css`
  position: relative;
  width: 100%;
`;

const closeButton = css`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 200ms ease;

  &:hover {
    background-color: #f9f6f4;
  }

  &:focus {
    box-shadow: 0 0 0 3px #ffdd00;
  }
`;

const closeButtonIcon = css`
  width: 16px;
  height: 16px;
  fill: #1a1a1a;
`;

function Header({
  FiltersButton,
  setSearchTerm,
  removeFilter,
  filters,
  requestId,
  isLoading,
  searchTerm,
}) {
  const isNotDesktop = useMediaQuery({ query: '(max-width: 1079px)' });
  const isMobile = useMediaQuery({ query: '(max-width: 799px)' });
  const { setMapState } = useMapState();
  const locationSearchRef = useRef(null);
  const rootElement = document.getElementById('root');
  const showLocationSearch = rootElement?.hasAttribute(
    'data-search-show-location-search',
  );
  const title = rootElement?.getAttribute('data-search-title') || '';
  const subtitle = rootElement?.getAttribute('data-search-subtitle') || '';
  const keywordFieldLabel =
    rootElement?.getAttribute('data-search-keyword-field-label') ||
    'What would you like to find';
  const locationFieldLabel =
    rootElement?.getAttribute('data-search-location-field-label') ||
    'Where would you like to find it';
  const shouldFocusInputRef = React.useRef(false);
  const lastSearchedTermRef = React.useRef('');
  const pendingScrollRef = React.useRef(false);
  const pendingSubmittedValueRef = React.useRef('');
  const searchType = getSearchType();
  // Staged locally so applying a filter can't commit a keyword that was never
  // submitted.
  const [keyword, setKeyword] = useState(searchTerm || '');
  const appliedTermRef = useRef(searchTerm || '');

  // Adopts a term applied outside the field: ?q= on load, back/forward.
  useEffect(() => {
    const appliedTerm = searchTerm || '';

    if (appliedTerm === appliedTermRef.current) {
      return;
    }

    appliedTermRef.current = appliedTerm;
    setKeyword(appliedTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (shouldFocusInputRef.current && !isLoading) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          document.getElementById('search-input')?.focus();
          shouldFocusInputRef.current = false;
        }, 50);
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && pendingScrollRef.current) {
      const inputField = document.getElementById('search-input');

      if (inputField?.scrollIntoView) {
        inputField.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      lastSearchedTermRef.current = pendingSubmittedValueRef.current || '';
      pendingScrollRef.current = false;
    }
  }, [isLoading, isMobile]);

  const handleSubmit = e => {
    e.preventDefault();

    const submittedValue = keyword.trim();

    if (submittedValue !== '') {
      ReactGA.event('search_term', {
        search_term: keyword,
        search_type: searchType,
      });
    }

    const locationValue = locationSearchRef.current?.getLocationValue();
    if (locationValue) {
      // Fit the map to the location's results: flag the pending fit
      // (MapStateContext survives list/map switches) and drop the viewport
      // filters so the response isn't clipped to the old view. MapView
      // consumes the flag when the response arrives.
      setMapState(prev => ({
        ...prev,
        pendingLocationFit: {
          token: Date.now(),
          requestIdAtSubmit: requestId,
        },
      }));
      const activeFilters = filters || [];
      MAP_VIEWPORT_FILTER_FIELDS.forEach(field => {
        if (activeFilters.some(f => f.field === field)) {
          removeFilter(field);
        }
      });
    }

    // Apply the staged location/distance before the keyword so the final
    // dispatch carries the complete filter state.
    locationSearchRef.current?.apply();

    // setSearchTerm clears all filters by default.
    setSearchTerm(keyword, { shouldClearFilters: false });

    if (isMobile && submittedValue !== lastSearchedTermRef.current) {
      pendingSubmittedValueRef.current = submittedValue;
      pendingScrollRef.current = true;
    }
  };

  const clearKeyword = () => {
    setKeyword('');

    // A never-submitted term was never searched, so clearing it must not
    // dispatch a request.
    if (appliedTermRef.current) {
      setSearchTerm('', { shouldClearFilters: false });
      shouldFocusInputRef.current = true;
      return;
    }

    document.getElementById('search-input')?.focus();
  };

  return (
    <div className={wrapper}>
      <div className={header}>
        <Container>
          <h1 className={titleStyle}>{title}</h1>
          {subtitle && (
            <div
              className={subTitleStyle}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />
          )}
          <div className={searchWrapper}>
            <form className={searchForm} onSubmit={handleSubmit}>
              <div className={searchInputWrapper}>
                <div className={searchInputInnerWrapper}>
                  <div className={fieldColumn}>
                    <label
                      className={fieldLabel}
                      dangerouslySetInnerHTML={{ __html: keywordFieldLabel }}
                      htmlFor="search-input"
                    />
                    <div className={searchInputContainer}>
                      <SearchIcon className={searchInputIcon} />
                      <FormTextInput
                        aria-labelledby="search-input"
                        className={searchInput}
                        id="search-input"
                        onChange={e => setKeyword(e.target.value)}
                        placeholder="Search"
                        type="text"
                        value={keyword}
                      />
                      <button
                        aria-label="Clear search"
                        className={closeButton}
                        onClick={clearKeyword}
                        type="button"
                      >
                        <CloseIcon className={closeButtonIcon} />
                      </button>
                    </div>
                  </div>
                  {showLocationSearch && (
                    <LocationSearch
                      actionsRef={locationSearchRef}
                      subtitle={locationFieldLabel}
                    />
                  )}
                  <Button
                    StartIcon={SearchIcon}
                    aria-label="Search"
                    className={searchFormButton}
                    type="submit"
                    variant="primary"
                  >
                    <span>Search</span>
                  </Button>
                </div>
              </div>
            </form>

            {isNotDesktop && FiltersButton}
          </div>
        </Container>
      </div>
    </div>
  );
}

export default withSearch(
  ({
    setSearchTerm,
    removeFilter,
    filters,
    requestId,
    isLoading,
    searchTerm,
  }) => ({
    setSearchTerm,
    removeFilter,
    filters,
    requestId,
    isLoading,
    searchTerm,
  }),
)(Header);
